import { signOut } from '../core/AccountGate';
import { handedness, type DominantHand } from '../input/Handedness';
import { seated } from '../input/Seated';

/**
 * Entry screen: pick a room, then join on a desktop or in a headset.
 *
 * It exists for two reasons beyond looking tidy. First, a browser will only start an
 * immersive session from a real user gesture, so there has to be a button — and by the time
 * it is pressed everything must already be built, or the gesture is spent on loading.
 * Second, it is where "is there a headset" gets answered honestly, before the user commits.
 *
 * Checks resolve behind it and the buttons enable as they land, so the screen doubles as a
 * loading readout rather than a modal in front of a spinner.
 *
 * It used to ask for a name too. It cannot any more, and should not: the name comes from
 * the account the session belongs to, and the server stamps it onto presence. The screen
 * shows which account that is rather than offering a box that would change nothing.
 *
 * **The room picker is the focus of this screen, not a setting.** More than one room per org
 * now exists (`380b21d`, `plan/identity-and-portal.md`), and until this task the only way to
 * choose one was the portal — this screen resolved exactly the one `GET /api/bootstrap`
 * handed it, with no way to see or reach any other. The owner, directly: "make the room
 * choice the focus of the in-headset page, not settings. We can have the settings as small
 * toggles at the top of the page, with a large room selector." Identity, dominant hand and
 * play style are `.splash-topbar` now — compact, not gone. Microphone permission has no button
 * of its own any more (`onMicRequested`'s own doc); only its status dot remains, in
 * `.splash-checks` alongside every other readiness check.
 *
 * **THE ROOM PICKER IS GONE, AND WITH IT THE LAST THING ON THIS SCREEN THAT WAS A CHOICE.** It
 * was already dead: SPEC-153 made rooms ephemeral — nobody creates, names or picks one — and
 * `setRooms` had had no production caller since, only tests. What replaced it as the thing you
 * choose is the lobby (SPEC-156): the room you arrive in, with your diagrams standing around
 * you as live miniatures. So this screen has one job now, which is the owner's own framing of
 * it: check the headset, ask which hand and whether you are standing, and offer one button.
 *
 * What that leaves is deliberately small — an entry point rather than a page. There is no
 * portal here, no diagram list, no management; the "Portal" link in the top bar goes to the
 * other host, and is a way out rather than a way in. Anything that would grow this screen back
 * into a menu belongs on the web app instead. Everything this screen exists to
 * build (scene, hub connection, XR session) is already built for the *previous* room by the
 * time a pick happens, and rebuilding all of it in place is a bigger job than a picker
 * justifies. `Router.ts` had a real, previously-unexercised bug in exactly this path —
 * `navigate('/room?roomId=X')` matched no registered route and silently fell through to
 * `fallback` — fixed alongside this screen; see its own doc comment.
 *
 * The visible copy is deliberately terse: this is the last thing a first-time, unguided
 * headset visitor reads before entering, and a paragraph per field does not survive being
 * read at a glance in VR. Two bullets survive the tidy-up and this task's own further cut
 * ("a lot of space is used by text that is pure fluff," the owner's words) because neither is
 * discoverable any other way: the microphone being live to the room by default is a privacy
 * fact, and the look-at hint is what lets every individual button binding go unstated here —
 * `input/ControlBindings.ts` is their one source of truth and the in-session look-at panel
 * already renders it (`17686ea`), so restating them would only be a copy that rots. Everything
 * else this screen used to say about who can see or hear you, or what grabbing does, was
 * genuinely just flavour text and is gone rather than moved into a comment — there was no
 * reasoning under it worth keeping.
 */

export type EntryMode = 'vr' | 'desktop';

export interface SplashChoice {
  mode: EntryMode;
}

export type CheckState = 'pending' | 'ready' | 'unavailable';

export interface CheckLine {
  id: string;
  label: string;
  state: CheckState;
}

export class Splash {
  private readonly root: HTMLElement;
  private readonly checksHost: HTMLElement;
  private readonly handButtons: HTMLButtonElement[];
  private readonly seatedButtons: HTMLButtonElement[];
  private readonly vrButton: HTMLButtonElement;
  private readonly desktopButton: HTMLButtonElement;
  private readonly errorHost: HTMLElement;

  private readonly checks = new Map<string, CheckLine>();
  private resolveChoice: ((choice: SplashChoice) => void) | null = null;

  private vrReady = false;
  private roomReady = false;

  /**
   * Fired on the Enter VR / Desktop mode press itself, before the choice resolves — no longer a
   * standalone button of its own. There used to be one ("Enable microphone," its own click,
   * deliberately separate from the entry press), but the owner reported it as dead weight once
   * the microphone started auto-acquiring on an already-granted browser (`main.ts`'s own
   * `navigator.permissions.query` check): "The microphone button on the landing page can be
   * removed since it's now not functional, and microphone state is indicated with a green light
   * just below." Removing it outright, though, would strand a *fresh* browser — one that has
   * never granted the permission — with no gesture left to raise the prompt from at all:
   * `getUserMedia`'s first-time prompt only renders in response to a real user gesture, and it
   * cannot render at all once inside an XR session (plan/ai-in-room.md, "Microphone permission
   * has to be granted before entering immersive mode"). Folding the request into this press
   * instead of the mic button's own click keeps that gesture: the Enter VR / Desktop mode click
   * *is* a user gesture, and it fires before `enterXRAsync` (or the desktop path) ever runs.
   *
   * `main.ts` is the caller — it does the actual `getUserMedia` call (through
   * `MicrophoneCapture.requestPermission()`), short-circuits to a resolved promise when a
   * permission answer is already known (an already-granted browser, or a previous press this
   * session), and reports the outcome through `setMicrophoneState` exactly as before — this
   * class only awaits whatever promise comes back, so the choice does not resolve out from under
   * a prompt still on screen. Returning nothing (`void`, not a rejected promise) is not
   * meaningful here: declining the prompt is not an error, the same "declining is not an error"
   * status `setMicrophoneState('unavailable', ...)` already reports honestly.
   */
  onMicRequested: (() => Promise<void>) | null = null;

  /**
   * Fired on the "Portal" link's own click — separate from the sign-out button beside it,
   * same arm's-length shape as `onMicRequested`. `main.ts` wires this to
   * `router.navigate('/portal')`; `Splash` itself knows nothing about routing.
   */
  onPortalRequested: (() => void) | null = null;

  /**
   * Fired on every Enter VR / Desktop mode press, once any microphone permission request has
   * settled — the same moment `waitForChoice()`'s own promise resolves, and not only the first
   * time. `waitForChoice()` exists for the one caller that needs a single settled promise before
   * anything else can be built (`main.ts`'s initial boot); this screen is now also the room
   * selector an ended VR session reveals again, and a Desktop-mode "leave" control returns to
   * (`plan/session-exit-and-membership-fixes.md`, A1/A2) — every choice made *there* has nowhere
   * else to resolve to, since the promise `waitForChoice()` handed out already settled on the
   * very first press. `main.ts` assigns this only once the initial choice has already been
   * acted on, so the first press — still resolved through `waitForChoice()` — never double-fires
   * whatever this is later set to.
   */
  onModeChosen: ((mode: EntryMode) => void) | null = null;

  constructor(host: HTMLElement, displayName: string) {
    this.root = document.createElement('div');
    this.root.className = 'splash';
    this.root.innerHTML = `
      <div class="splash-panel">
        <header class="splash-topbar">
          <div class="splash-identity">
            <small data-account></small>
            <button type="button" class="splash-link" data-portal>Portal</button>
            <button type="button" class="splash-link" data-signout>Sign out</button>
          </div>

          <div class="splash-toggle" role="group" aria-label="Dominant hand">
            <button type="button" data-hand="left">Left</button>
            <button type="button" data-hand="right">Right</button>
          </div>

          <div class="splash-toggle" role="group" aria-label="Play style">
            <button type="button" data-seated="false">Standing</button>
            <button type="button" data-seated="true">Seated</button>
          </div>
        </header>

        <ul class="splash-checks" aria-live="polite"></ul>

        <ul class="splash-guide">
          <li>Your microphone is live to the room by default.</li>
          <li>Look at your controller any time to see what its buttons do.</li>
        </ul>

        <div class="splash-actions">
          <button type="button" data-mode="vr" class="primary" disabled>Enter lobby</button>
          <button type="button" data-mode="desktop" disabled>Enter on desktop</button>
        </div>

        <p class="splash-error" hidden></p>
      </div>
    `;

    host.appendChild(this.root);

    this.checksHost = this.query('.splash-checks');
    this.handButtons = [...this.root.querySelectorAll<HTMLButtonElement>('[data-hand]')];
    this.seatedButtons = [...this.root.querySelectorAll<HTMLButtonElement>('[data-seated]')];
    this.vrButton = this.query('[data-mode="vr"]');
    this.desktopButton = this.query('[data-mode="desktop"]');
    this.errorHost = this.query('.splash-error');

    this.query('[data-account]').textContent = displayName;

    this.query<HTMLButtonElement>('[data-portal]').addEventListener('click', () =>
      this.onPortalRequested?.(),
    );
    this.query<HTMLButtonElement>('[data-signout]').addEventListener('click', () => void signOut());

    // Applied immediately rather than on entry: the XR session is built while this screen is
    // up, and teleportation has to know which controller it belongs to. (Manipulation lives
    // on the dominant hand set here; travel and the menu are always on the other one — see
    // input/ControlBindings.ts and the in-session look-at panel for the full binding table.)
    for (const button of this.handButtons) {
      button.addEventListener('click', () => {
        handedness.set(button.dataset.hand === 'left' ? 'left' : 'right');
        this.renderHandedness();
      });
    }

    this.renderHandedness();

    // Applied immediately, same as handedness. Unlike handedness, nothing in the client
    // currently reads this value at all — see input/Seated.ts's own docstring for why — but
    // it is still set the same way, immediately and persisted, since the setting is a stated
    // requirement kept for a future consumer, not something this screen has any reason to
    // treat differently from the toggle beside it.
    for (const button of this.seatedButtons) {
      button.addEventListener('click', () => {
        seated.set(button.dataset.seated === 'true');
        this.renderSeated();
      });
    }

    this.renderSeated();

    this.vrButton.addEventListener('click', () => this.submit('vr'));
    this.desktopButton.addEventListener('click', () => this.submit('desktop'));

    this.root.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') {
        return;
      }

      // Enter takes the best available option, so a headset user never has to reach for a
      // mouse they are not holding.
      event.preventDefault();
      this.submit(this.vrReady ? 'vr' : 'desktop');
    });
  }

  /** Resolves once a mode is chosen. */
  waitForChoice(): Promise<SplashChoice> {
    return new Promise((resolve) => {
      this.resolveChoice = resolve;
    });
  }

  /** Adds or updates a status line. Order of first appearance is preserved. */
  setCheck(id: string, label: string, state: CheckState): void {
    this.checks.set(id, { id, label, state });
    this.renderChecks();
  }

  /**
   * Reports the microphone permission outcome — reuses `setCheck`'s own rendering for the
   * status line (so it reads in the same "backend / room / headset" language), the green dot
   * beside "microphone" the owner pointed at as the indicator that should stay once the
   * standalone button went away ("microphone state is indicated with a green light just
   * below"). No button of its own left to disable or relabel here any more — `onMicRequested`'s
   * own doc has the full reasoning for where the request itself now happens.
   */
  setMicrophoneState(state: CheckState, label: string): void {
    this.setCheck('microphone', label, state);
  }

  /**
   * Whether an immersive session can be started. Drives the VR button, and swaps in an
   * explanation when it cannot — "no headset" is information, not an error.
   */
  setVrAvailable(available: boolean): void {
    this.vrReady = available;

    this.vrButton.textContent = available ? 'Enter lobby' : 'VR unavailable';
    this.vrButton.classList.toggle('primary', available);
    this.desktopButton.classList.toggle('primary', !available);

    this.updateButtons();
  }

  /** Enables entry. Held back until the room is built, so entry is instant. */
  setRoomReady(ready: boolean): void {
    this.roomReady = ready;
    this.updateButtons();
  }

  showError(message: string): void {
    this.errorHost.textContent = message;
    this.errorHost.hidden = false;
  }

  /** Re-enables the screen after a failed entry attempt, so it can be retried. */
  reopen(): void {
    this.root.classList.remove('splash-hidden');
    this.updateButtons();
  }

  hide(): void {
    this.root.classList.add('splash-hidden');
  }

  dispose(): void {
    this.root.remove();
  }

  private submit(mode: EntryMode): void {
    if (!this.roomReady || (mode === 'vr' && !this.vrReady)) {
      return;
    }

    this.errorHost.hidden = true;

    // Disabled rather than hidden: entering VR can fail (headset asleep, permission
    // dismissed), and the screen has to be able to come back.
    this.vrButton.disabled = true;
    this.desktopButton.disabled = true;

    // `onMicRequested`'s own doc has the full reasoning: this is the one remaining user gesture
    // that can still raise a first-time `getUserMedia` prompt, so it is asked — and awaited —
    // here, before the choice resolves, rather than left to whatever happens once inside (or
    // heading into) a session. Called unconditionally; `main.ts`'s own implementation is what
    // short-circuits to an already-resolved promise once a permission answer is already known,
    // not this class.
    // `onMicRequested`'s own doc has the full reasoning: this is the one remaining user gesture
    // that can still raise a first-time `getUserMedia` prompt, so it is asked — and awaited —
    // here, before the choice resolves, rather than left to whatever happens once inside (or
    // heading into) a session. Called unconditionally; `main.ts`'s own implementation is what
    // short-circuits to an already-resolved promise once a permission answer is already known,
    // not this class.
    //
    // Both callbacks fire from the same settle point: `resolveChoice` for the one-shot initial
    // promise (a no-op once already settled — see `onModeChosen`'s own doc for why calling both
    // unconditionally on every press is exactly right rather than a double-fire).
    const proceed = () => {
      this.resolveChoice?.({ mode });
      this.onModeChosen?.(mode);
    };
    const micRequest = this.onMicRequested?.();

    if (micRequest) {
      void micRequest.finally(proceed);
    } else {
      proceed();
    }
  }

  private updateButtons(): void {
    this.desktopButton.disabled = !this.roomReady;
    this.vrButton.disabled = !this.roomReady || !this.vrReady;
  }

  private renderHandedness(): void {
    const current: DominantHand = handedness.dominant;

    for (const button of this.handButtons) {
      const selected = button.dataset.hand === current;

      button.classList.toggle('selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    }
  }

  private renderSeated(): void {
    const current = seated.value;

    for (const button of this.seatedButtons) {
      const selected = (button.dataset.seated === 'true') === current;

      button.classList.toggle('selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    }
  }

  private renderChecks(): void {
    this.checksHost.replaceChildren(
      ...[...this.checks.values()].map((check) => {
        const item = document.createElement('li');
        item.className = `check check-${check.state}`;
        item.innerHTML = `<span class="dot"></span><span>${escapeHtml(check.label)}</span>`;
        return item;
      }),
    );
  }

  private query<T extends HTMLElement>(selector: string): T {
    const found = this.root.querySelector<T>(selector);

    if (!found) {
      throw new Error(`Splash markup is missing ${selector}.`);
    }

    return found;
  }
}

function escapeHtml(value: string): string {
  const element = document.createElement('span');
  element.textContent = value;
  return element.innerHTML;
}
